import type { SiteContentOverlay } from '../storefront-content';
import type { SiteField, SiteManifest } from './types';

/**
 * Typed accessor over (manifest defaults ⟵ stored overlay). Unknown paths
 * resolve to ''/[]/undefined — a typo shows up visually, never throws.
 */
export type ResolvedSiteContent = {
  text(path: string): string;
  /** richParas / stringList rows. */
  paras(path: string): string[];
  /** objectList rows. */
  items(path: string): Array<Record<string, string>>;
  /** Uploaded R2 key for an image field, if any. */
  imageKey(path: string): string | undefined;
  /** The field's baked fallback URL/path. */
  imageDefault(path: string): string;
  /**
   * True when the admin has EXPLICITLY customized this field — the overlay
   * holds a value for it — as opposed to leaving it on the manifest default.
   *
   * For list fields this is a deliberate user action, not a heuristic: the Site
   * Pages editor writes the key only on "Customize" and deletes it on "Reset to
   * default". Callers use it to decide between the manifest and some other
   * pre-existing source of the same content. Unknown paths → false.
   */
  isCustomized(path: string): boolean;
};

export function indexManifestFields(manifest: SiteManifest): Map<string, SiteField> {
  const map = new Map<string, SiteField>();
  for (const g of manifest.groups)
    for (const s of g.sections)
      for (const f of s.fields) map.set(`${g.id}.${s.id}.${f.id}`, f);
  return map;
}

/** How richParas/stringList rows are stored inside `overlay.lists`. */
export const LIST_TEXT_KEY = 'text';

export function resolveSiteContent(
  manifest: SiteManifest,
  overlay: SiteContentOverlay | undefined,
): ResolvedSiteContent {
  const fields = indexManifestFields(manifest);
  return {
    text(path) {
      const o = overlay?.text?.[path];
      if (typeof o === 'string') return o;
      const f = fields.get(path);
      return f && (f.kind === 'text' || f.kind === 'textarea' || f.kind === 'select')
        ? f.default
        : '';
    },
    paras(path) {
      const rows = overlay?.lists?.[path];
      if (Array.isArray(rows)) return rows.map((r) => r[LIST_TEXT_KEY] ?? '');
      const f = fields.get(path);
      return f && (f.kind === 'richParas' || f.kind === 'stringList') ? [...f.default] : [];
    },
    items(path) {
      const rows = overlay?.lists?.[path];
      if (Array.isArray(rows)) return rows.map((r) => ({ ...r }));
      const f = fields.get(path);
      return f && f.kind === 'objectList' ? f.default.map((r) => ({ ...r })) : [];
    },
    imageKey(path) {
      return overlay?.images?.[path] || undefined;
    },
    imageDefault(path) {
      const f = fields.get(path);
      return f && f.kind === 'image' ? f.defaultUrl : '';
    },
    isCustomized(path) {
      const f = fields.get(path);
      if (!f) return false;
      if (f.kind === 'image') return !!overlay?.images?.[path];
      if (f.kind === 'objectList' || f.kind === 'richParas' || f.kind === 'stringList')
        return Array.isArray(overlay?.lists?.[path]);
      return typeof overlay?.text?.[path] === 'string';
    },
  };
}
